import React from 'react';
import { Target, Eye, Award, Factory, Users, Heart, TrendingUp, CheckCircle } from 'lucide-react';

const About = () => {
  const milestones = [
    { year: '2021', title: 'Kuruluş', description: 'Pekala Süt Ürünleri olarak üretim yolculuğumuza başladık' },
    { year: '2022', title: 'Kapasite Artışı', description: 'Üretim kapasitemizi 2 katına çıkardık' },
    { year: '2023', title: 'Ürün Çeşitliliği', description: 'Taze içecek ve süt ürünleri gamımızı genişlettik' },
    { year: '2024', title: 'Bölgesel Lider', description: '1000+ mutlu müşteriye ulaştık' },
  ];

  const values = [
    {
      icon: <CheckCircle className="h-8 w-8" />,
      title: 'Kalite',
      description: '100% doğal içeriklerle, en yüksek kalite standartlarında üretim yapıyoruz.'
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: 'Güven',
      description: 'Müşterilerimizin güvenini kazanmak ve korumak en büyük önceliğimiz.'
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Müşteri Odaklılık',
      description: 'Müşteri memnuniyeti bizim için her şeyden önemlidir.'
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: 'Sürekli Gelişim',
      description: 'Teknolojiye yatırım yaparak sürekli kendimizi geliştiriyoruz.'
    }
  ];

  const certificates = [
    { name: 'TS 1330', description: 'Türk Standardı - Ayran Standardı' },
    { name: 'Yerli Üretim', description: '100% Yerli Üretim Belgesi' },
    { name: 'Türk Malı', description: 'Türk Malı Sertifikası' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#216DB8] to-blue-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Hakkımızda
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed">
              Doğallığın ve lezzetin âlâsını sofranıza taşıyoruz
            </p>
          </div>
        </div>
      </div>

      {/* Our Story */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Hikayemiz</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  <strong className="text-[#216DB8]">2021 yılında</strong> kurulan Pekala Süt Ürünleri, 
                  Şanlıurfa'nın Eyyubiye ilçesinde modern tesislerimizde üretim yapmaktadır. 
                  Arjin Tekstil Petrol San. Tic. Ltd. Şti.'nin ASMİN SÜT ŞUBESİ olarak faaliyetlerimizi sürdürmekteyiz.
                </p>
                <p>
                  Kuruluşumuzdan bu yana, doğal ve kaliteli süt ürünleri ile taze içecekleri tüketicilerimizle 
                  buluşturma misyonuyla yola çıktık. <strong className="text-[#216DB8]">%100 Yerli Üretim</strong> anlayışıyla, 
                  tamamen yerli kaynaklardan üretim yaparak <strong className="text-[#216DB8]">Türk Malı</strong> sertifikasına sahip 
                  ürünlerimizle gurur duyuyoruz. Kısa sürede bölgemizin güvenilir markalarından biri haline geldik.
                </p>
                <p>
                  Bugün, Koçören 2. Organize Sanayi Bölgesi'ndeki modern tesislerimizde, 
                  <strong className="text-[#216DB8]"> TS 1330 Ayran Standardı</strong> başta olmak üzere 
                  en yüksek hijyen standartlarında üretim yaparak binlerce ailenin sofrasına lezzet katıyoruz.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#216DB8] to-green-600 rounded-2xl p-8 text-white">
                <Factory className="h-16 w-16 mb-6" />
                <h3 className="text-3xl font-bold mb-4">Modern Tesis</h3>
                <p className="text-blue-100 mb-6">
                  Son teknoloji ekipmanlarla donatılmış tesisimizde 7/24 üretim yaparak 
                  taze ürünleri zamanında müşterilerimize ulaştırıyoruz. TS 1330 standardına 
                  uygun üretim yapıyoruz.
                </p>
                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/20">
                  <div>
                    <div className="text-3xl font-bold">24/7</div>
                    <div className="text-sm text-blue-100">Üretim</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">100%</div>
                    <div className="text-sm text-blue-100">Doğal</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">100%</div>
                    <div className="text-sm text-blue-100">Yerli</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {/* Mission */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-[#216DB8] hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-blue-100 rounded-full p-4">
                  <Target className="h-8 w-8 text-[#216DB8]" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800">Misyonumuz</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                En yüksek kalite standartlarında, tamamen doğal içeriklerle ürettiğimiz süt ürünleri ve 
                taze içecekleri tüketicilerimize ulaştırarak, sağlıklı ve lezzetli beslenmeye katkıda bulunmak. 
                Çiftlikten sofranıza kadar her aşamada kaliteyi ve güveni ön planda tutarak, ailelerinizin 
                sağlığına değer katmak.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-green-600 hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-green-100 rounded-full p-4">
                  <Eye className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800">Vizyonumuz</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Türkiye'nin en güvenilir ve tercih edilen süt ürünleri markalarından biri olmak. 
                Sürekli gelişim ve yeniliğe açık yaklaşımımızla, sektörde öncü bir konuma ulaşmak. 
                Bölgesel liderliğimizi ulusal çapta yaygınlaştırarak, her evde Pekala kalitesini 
                sofralara taşımak.
              </p>
            </div>
          </div>

          {/* Our Values */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Değerlerimiz</h2>
              <p className="text-gray-600 text-lg">Bizi biz yapan temel prensipler</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <div className="bg-gradient-to-br from-[#216DB8] to-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Kilometre Taşlarımız</h2>
              <p className="text-gray-600 text-lg">Büyüme ve gelişim yolculuğumuz</p>
            </div>
            <div className="relative">
              {/* Timeline Line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-[#216DB8] to-green-600"></div>
              
              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <div 
                    key={index}
                    className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    {/* Content */}
                    <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                      <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                        <div className="text-3xl font-bold text-[#216DB8] mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </div>
                    </div>
                    
                    {/* Center Circle */}
                    <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#216DB8] rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Production Process */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Üretim Sürecimiz</h2>
              <p className="text-gray-600 text-lg">Çiftlikten sofranıza kalite yolculuğu</p>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '01', title: 'Hammadde Seçimi', desc: 'En kaliteli doğal hammaddeler' },
                { step: '02', title: 'Modern Üretim', desc: 'Son teknoloji tesislerimizde işleme' },
                { step: '03', title: 'Kalite Kontrol', desc: 'Titiz laboratuvar testleri' },
                { step: '04', title: 'Teslimat', desc: 'Taze olarak sofranıza ulaşır' },
              ].map((process, index) => (
                <div key={index} className="relative">
                  <div className="bg-gradient-to-br from-[#216DB8] to-blue-800 rounded-xl p-6 text-white text-center">
                    <div className="text-5xl font-bold opacity-20 mb-2">{process.step}</div>
                    <h3 className="text-xl font-bold mb-3">{process.title}</h3>
                    <p className="text-blue-100 text-sm">{process.desc}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-[#216DB8] to-transparent"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Certificates & Awards */}
          <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-12 text-white">
            <div className="text-center mb-12">
              <Award className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-4xl font-bold mb-4">Sertifikalar ve Belgelerimiz</h2>
              <p className="text-green-100 text-lg">Kalitemizin tescilli kanıtları</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {certificates.map((cert, index) => (
                <div 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all"
                >
                  <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    <Award className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{cert.name}</h3>
                  <p className="text-green-100 text-sm">{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
